import { useContext, useState, useEffect, useRef } from 'react';
import logoLight from '@assets/images/scriptix-blue-logo@2-1.png';
import logoDark from '@assets/images/scriptix-white-400x99.png';
import { PATHS } from '../../router/paths.ts';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { ThemeContext } from '@utils/providers/theme-context.tsx';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';
import Loader from '@components/atoms/loaders/loader.tsx';

interface Props {
  isLink: boolean;
  imgClassName?: string;
}

export default function Logo({ isLink, imgClassName }: Props) {
  const navigate = useNavigate();
  const theme = useContext(ThemeContext)?.theme ?? 'light';
  const { data: organization, isLoading, isError } = useOrganization();

  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const lastRenderedLogo = useRef<string | undefined>();

  const orgLogoRaw = organization?.reseller_branding?.brand_logo;
  const hasOrgLogo =
    typeof orgLogoRaw === 'string' && orgLogoRaw.trim().length > 0;
  const orgLogo = hasOrgLogo ? orgLogoRaw!.trim() : null;

  const themedDefault = theme === 'dark' ? logoDark : logoLight;

  const currentLogo = orgLogo ?? themedDefault;

  // For shared pages or when there's an error, don't show loading state
  const shouldShowLoader = isLoading && !isError;

  useEffect(() => {
    if (currentLogo !== lastRenderedLogo.current) {
      setIsImageLoaded(false);
      lastRenderedLogo.current = currentLogo;
    }
  }, [currentLogo]);

  const handleClick = () => {
    if (isLink) navigate(PATHS().HOME);
  };

  return (
    <a
      className="flex justify-center"
      href={PATHS().HOME}
      onClick={e => e.preventDefault()}
    >
      {shouldShowLoader ? (
        <div className="w-36 flex justify-center text-center">
          <Loader width={1} height={10} />
        </div>
      ) : (
        <img
          key={currentLogo}
          className={cn(
            imgClassName ?? 'h-8 w-auto',
            isLink && 'cursor-pointer',
            'dark:invert-0 dark:mix-blend-normal dark:brightness-100'
          )}
          src={currentLogo}
          alt="Organization or Default Logo"
          onClick={handleClick}
          onLoad={() => setIsImageLoaded(true)}
          onError={() => {
            if (!hasOrgLogo && currentLogo !== logoLight) {
              lastRenderedLogo.current = logoLight;
              setIsImageLoaded(false);
            }
          }}
          style={{
            opacity: isImageLoaded ? 1 : 0,
            transition: 'opacity 0.2s ease-in'
          }}
        />
      )}
    </a>
  );
}
